import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import type { User } from "@/lib/auth/auth-api";

import { useAuthActions } from "@/features/auth/shared/auth-actions";
import {
  AuthContext,
  type AuthContextValue,
} from "@/features/auth/shared/auth-context-value";
import * as authApi from "@/lib/auth/auth-api";
import { clearStoredToken, getStoredToken } from "@/lib/auth/token-storage";

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Owns the current session: bootstraps it from a stored token on mount,
 * exposes login/signup/logout, and is the single source of truth for
 * "am I logged in" / "who am I" (backed by `GET /v1/auth/me`). Never throws
 * on a failed bootstrap — an invalid/expired stored token is treated as
 * "not authenticated", not a crash.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const authActions = useAuthActions({ queryClient, setUser });

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!getStoredToken()) {
        if (!cancelled) setIsLoading(false);
        return;
      }
      try {
        const me = await authApi.fetchMe();
        if (!cancelled) setUser(me);
      } catch {
        clearStoredToken();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      ...authActions,
    }),
    [authActions, isLoading, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
